// 自動再生可否をプログラムで判定するユーティリティ。
// muted な <video> を生成して play() を試み、resolve すれば自動再生可、reject（NotAllowedError 等）
// すれば不可と判定する。判定は video の autoplay ポリシー（ブラウザ全体設定）に従うため、本番の
// <video autoPlay muted playsInline> の挙動と一致する。SSR・旧ブラウザ・タイムアウト・reject は
// いずれも安全側（不可）に倒し、判定できないときは従来どおり transfer シーンを経由させる。

// 判定用の最小 mp4（64x64・約0.12秒・音声トラック無し・H.264/AVC）。ダミーの無効なバイト列では
// なく、ffmpeg で生成した「正規にデコード・再生できる本物の mp4」である点が重要。
//
// 生成コマンド（下記バイト列に埋め込まれた mp4 内部メタデータ ― Lavf/Lavc バージョン・avcC の
// profile/level・x264 の options 文字列・box 配置・実フレーム内容 ― から復元。このコマンドの出力は
// 上記バイト列と SHA256 一致することを確認済み）:
//   ffmpeg -f lavfi -i color=c=black:s=64x64:r=25:d=0.12 \
//     -c:v libx264 -profile:v baseline -level 3.0 -pix_fmt yuv420p \
//     -movflags +faststart -an out.mp4
//   ※ -i color=black で純黒 64x64、r=25 と d=0.12 で 25fps・3 フレーム（0.12 秒）を生成。
//   ※ preset / crf は既定（medium / crf=23.0）のため明示指定なし。
//   ※ 再生成する場合、Lavf/Lavc・libx264 のバージョン差で数バイト変わりうる（判定挙動には影響しない）。
//

// なぜこのバイト列で自動再生可否を判定できるか:
//   1. ブラウザの自動再生ポリシーは play() を呼んだ瞬間に評価される。判定材料はミュート状態・
//      ユーザー操作履歴・サイト設定・省電力モード等であり、動画の中身（尺・解像度・内容）には
//      依存しない。したがって中身は最小限でよい。
//   2. ただし play() の Promise は「ポリシーで拒否されたとき（NotAllowedError）」だけでなく
//      「デコード失敗・読み込み失敗のとき」にも reject し、Promise 上でこれらは区別できない。
//      そこで後者を設計で排除する:
//        - 実際にデコードできる正規の mp4 を使う → デコードエラーを排除（本物の mp4 が必要な理由）
//        - data URI として埋め込む → ネットワーク取得を無くし読み込みエラーを排除
//      結果、reject の原因を自動再生ポリシー拒否にほぼ限定できる。
//   3. muted かつ音声トラック無しなので、本番の <video autoPlay muted playsInline> と同じ
//      「ミュート自動再生」の許可条件で評価される。よって本 probe の結果が本番の挙動と一致する。
//   4. 最小サイズ・data URI にするのは、ネットワークやアプリ個別アセットに依存させず、
//      タイムアウト内に即座に解決させるため。
const PROBE_VIDEO_DATA_URI =
  'data:video/mp4;base64,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';

// 判定がハングしないためのタイムアウト（ミリ秒）。この時間内に解決しなければ不可扱いにする。
const PROBE_TIMEOUT_MS = 600;

// 生成した <video> の play() を試み、自動再生可否を boolean で返す。
function probeAutoplay(): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const video = document.createElement('video');
    video.muted = true;
    video.setAttribute('muted', '');
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.src = PROBE_VIDEO_DATA_URI;

    let playResult: Promise<void> | undefined;
    try {
      playResult = video.play();
    } catch {
      resolve(false);
      return;
    }

    // play() が Promise を返さない旧ブラウザは判定不能。安全側（不可）に倒す。
    if (playResult === undefined || typeof playResult.then !== 'function') {
      resolve(false);
      return;
    }

    playResult
      .then(() => {
        video.pause();
        resolve(true);
      })
      .catch(() => {
        resolve(false);
      });
  });
}

// 自動再生が可能なら true、不可・判定不能なら false を返す。
export async function canAutoplayVideo(): Promise<boolean> {
  // SSR では判定できないため安全側（不可）に倒す。
  if (typeof document === 'undefined') {
    return false;
  }

  // 判定が早く解決した場合にタイマーを残さないよう、race 確定後に必ず clearTimeout する。
  let timerId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<boolean>((resolve) => {
    timerId = setTimeout(() => resolve(false), PROBE_TIMEOUT_MS);
  });

  try {
    return await Promise.race([probeAutoplay(), timeout]);
  } finally {
    if (timerId !== undefined) {
      clearTimeout(timerId);
    }
  }
}
